import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Building2, ChevronDown, Lock, LogOut, Menu, Moon, Sun } from 'lucide-react';
import { Button } from '@easyflow/ui-master/components/ui/button';
import { buildCrmUrl } from '@easyflow/shared';
import NotificationBell from '@/components/NotificationBell';
import { useAuth, type User } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';
import MasterMobileNav from './MasterMobileNav';
import StorageQuotaWidget from './StorageQuotaWidget';
import { navGroups } from './nav-groups';
import { findActiveNavItem } from './nav-matching';

function userPresentation(user: User | null): { name: string; initials: string } {
  const first = user?.firstName.trim() ?? '';
  const last = user?.lastName.trim() ?? '';
  const email = user?.email.trim() ?? '';
  const name = first || last ? `${first}${first && last ? ' ' : ''}${last}` : email || 'Master Admin';
  const initials = first && last
    ? `${first[0] ?? ''}${last[0] ?? ''}`
    : first ? first.slice(0, 2)
      : email ? (email.split('@')[0] ?? 'MA').slice(0, 2)
        : 'MA';
  return { name, initials: initials.toUpperCase() };
}

export default function MasterTopbar(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const mobileMenuButtonRef = useRef<HTMLButtonElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const [theme, setTheme] = useState<'dark' | 'light'>(() =>
    typeof window !== 'undefined' && localStorage.getItem('master-theme') === 'light' ? 'light' : 'dark'
  );
  const activeItem = findActiveNavItem(location.pathname, navGroups);
  const profile = userPresentation(user);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
    localStorage.setItem('master-theme', theme);
  }, [theme]);

  useEffect(() => {
    if (!userMenuOpen) return;
    const closeOnOutsideClick = (event: MouseEvent): void => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) setUserMenuOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setUserMenuOpen(false);
    };
    document.addEventListener('mousedown', closeOnOutsideClick);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeOnOutsideClick);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [userMenuOpen]);

  const toggleTheme = (): void => setTheme((current) => current === 'dark' ? 'light' : 'dark');
  const handleLogout = (): void => { void logout(); };
  const closeMobileMenu = (): void => {
    setMobileMenuOpen(false);
    requestAnimationFrame(() => mobileMenuButtonRef.current?.focus());
  };
  const pageTitle = activeItem?.name ?? 'Master Control';

  return (
    <header className="sticky top-0 z-40 h-[76px] border-b border-border/80 bg-white/80 backdrop-blur-xl dark:bg-[#101c30]/80 dark:border-white/10" role="banner">
      <div className="flex h-full items-center justify-between px-4 min-[760px]:px-5 min-[1180px]:px-7">
        <div className="flex min-w-0 items-center gap-3">
          <Button
            ref={mobileMenuButtonRef}
            variant="ghost"
            size="icon"
            onClick={() => setMobileMenuOpen(true)}
            className="shrink-0 min-[760px]:hidden"
            aria-label="Apri menu navigazione"
            aria-expanded={mobileMenuOpen}
            aria-controls="master-mobile-navigation"
          >
            <Menu className="h-5 w-5" />
          </Button>
          <div className="min-w-0">
            <h1 className="truncate text-lg font-semibold tracking-tight">{pageTitle}</h1>
            <p className="hidden text-[11px] text-muted-foreground min-[480px]:block">Master Control / {pageTitle}</p>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1 sm:gap-2">
          {user?.role === 'super_admin' && (
            <Button variant="ghost" size="sm" onClick={() => window.location.assign(buildCrmUrl('/'))} className="hidden gap-1.5 text-purple-700 hover:text-purple-800 dark:text-purple-200 dark:hover:text-white sm:inline-flex" aria-label="Apri EasyCRM">
              <Building2 className="h-4 w-4" /><span>EasyCRM</span><ChevronDown className="h-3.5 w-3.5" />
            </Button>
          )}
          <Button variant="ghost" size="icon" onClick={toggleTheme} className="hidden sm:inline-flex" title={theme === 'dark' ? 'Passa a Light Mode' : 'Passa a Dark Mode'} aria-label={theme === 'dark' ? 'Passa a Light Mode' : 'Passa a Dark Mode'} aria-pressed={theme === 'dark'}>
            {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </Button>
          <div className="hidden min-[900px]:block"><StorageQuotaWidget /></div>
          <NotificationBell />
          <div className="relative ml-1" ref={userMenuRef}>
            <button type="button" onClick={() => setUserMenuOpen((open) => !open)} className="flex items-center gap-1 rounded-full p-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500" aria-label="Apri menu utente" aria-haspopup="menu" aria-expanded={userMenuOpen}>
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-purple-500 to-pink-500 text-sm font-semibold text-white">{profile.initials}</span>
              <ChevronDown className={cn('hidden h-3.5 w-3.5 text-muted-foreground sm:block', userMenuOpen && 'rotate-180')} />
            </button>
            {userMenuOpen && (
              <div className="absolute right-0 top-full z-50 mt-2 w-64 rounded-xl border border-border bg-white py-1 shadow-xl dark:border-white/10 dark:bg-slate-900" role="menu">
                <div className="border-b border-border px-4 py-3 dark:border-white/10"><p className="truncate text-sm font-medium">{profile.name}</p><p className="mt-0.5 text-[11px] text-purple-600 dark:text-purple-300">Super Administrator</p></div>
                <button type="button" onClick={() => { setUserMenuOpen(false); navigate('/account/security'); }} className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm hover:bg-purple-50 dark:hover:bg-purple-500/10" role="menuitem"><Lock className="h-4 w-4" />Sicurezza</button>
                <div className="my-1 h-px bg-border dark:bg-white/10" />
                <button type="button" onClick={() => { setUserMenuOpen(false); handleLogout(); }} className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-500/10" role="menuitem"><LogOut className="h-4 w-4" />Logout</button>
              </div>
            )}
          </div>
        </div>
      </div>
      <MasterMobileNav open={mobileMenuOpen} onClose={closeMobileMenu} theme={theme} onToggleTheme={toggleTheme} onLogout={handleLogout} user={user} />
    </header>
  );
}